import React from "react";
import { Box, Typography } from "@mui/material";
import { getSlotSymbolImage, SLOT_SYMBOL_IMAGES } from "../../../../../static/Constants";
import { getSymbolById } from "./threeSlotConfig";

/**
 * 3-Slot Symbol Image Renderer
 * Renders the high-definition 3D PNG gem/crown symbols with larger proportions,
 * clean transparent backdrop to eliminate excess white space, and glowing animation.
 */
export const ThreeSlotSymbolIcon = ({
  symbol,
  size = "md",
  isWinning = false,
  hasRollerBg = false,
}) => {
  if (!symbol) return null;

  const isSmall = size === "sm";
  const isLarge = size === "lg";
  const isXLarge = size === "xl";

  // Responsive container and image dimensions (significantly enlarged)
  const containerSize = isSmall
    ? 48
    : isLarge
    ? { xs: 110, sm: 130, md: 146 }
    : isXLarge
    ? { xs: 130, sm: 155, md: 170 }
    : { xs: 80, sm: 94, md: 104 };

  const imageSize = isSmall
    ? 42
    : isLarge
    ? { xs: 102, sm: 122, md: 138 }
    : isXLarge
    ? { xs: 122, sm: 146, md: 160 }
    : { xs: 74, sm: 88, md: 98 };

  // Resolve symbol image through centralized single source of truth
  const symbolImg =
    getSlotSymbolImage(symbol) ||
    (symbol.id ? SLOT_SYMBOL_IMAGES[symbol.id] : null) ||
    getSymbolById(symbol.id)?.image;

  return (
    <Box
      sx={{
        width: containerSize,
        height: containerSize,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        borderRadius: "18px",
        background: isWinning
          ? "radial-gradient(circle, rgba(254, 240, 138, 0.4) 0%, rgba(234, 179, 8, 0.12) 65%, transparent 100%)"
          : hasRollerBg
          ? "linear-gradient(180deg, #e2e8f0 0%, #f8fafc 18%, #ffffff 50%, #f8fafc 82%, #cbd5e1 100%)"
          : "transparent",
        border: isWinning
          ? "2.5px solid #eab308"
          : hasRollerBg
          ? "1.5px solid rgba(203, 213, 225, 0.8)"
          : "none",
        boxShadow: isWinning
          ? "0 0 24px rgba(234, 179, 8, 0.75), inset 0 0 12px rgba(254, 240, 138, 0.55)"
          : hasRollerBg
          ? "inset 0 3px 6px rgba(255, 255, 255, 0.9), inset 0 -3px 6px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.08)"
          : "none",
        animation: isWinning ? "winningPulse 0.8s ease-in-out infinite alternate" : "none",
        "@keyframes winningPulse": {
          "0%": { transform: "scale(1)", filter: "drop-shadow(0 0 6px rgba(250, 204, 21, 0.7))" },
          "100%": { transform: "scale(1.08)", filter: "drop-shadow(0 0 18px rgba(250, 204, 21, 1))" },
        },
      }}
    >
      {symbolImg ? (
        <Box
          component="img"
          src={symbolImg}
          alt={symbol.label || symbol.id || "Symbol"}
          sx={{
            width: imageSize,
            height: imageSize,
            objectFit: "contain",
            filter: isWinning
              ? "drop-shadow(0 0 10px rgba(234, 179, 8, 0.9))"
              : "drop-shadow(0 5px 12px rgba(0, 0, 0, 0.22))",
            userSelect: "none",
            pointerEvents: "none",
            transition: "all 0.2s ease",
          }}
        />
      ) : (
        <Typography sx={{ fontSize: isSmall ? "1.4rem" : isLarge ? "3.2rem" : "2.2rem" }}>
          {symbol.symbolText || symbol.iconText || "🎰"}
        </Typography>
      )}
    </Box>
  );
};

export default ThreeSlotSymbolIcon;
